/**
 * Shared utilities for proposal components.
 *
 * Migrated from cards/shared.tsx for use across all renderers.
 */

import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import {
  type EntityIconComponent,
  getEntityTypeIconComponent,
} from '@/lib/crm/utils/icons/EntityTypeIcon';
import type {
  AssociationAddedEntityPayload,
  AssociationRemovedEntityPayload,
  EnrichedActionData,
  ProposalListItem,
  ProposedAction,
} from '@/lib/proposals/core/types';
import {
  isAssociationPayload,
  isTimelineEventPayload,
} from '@/lib/proposals/core/types';
import { MIN_TABLE_VIEW_ACTIONS } from '@/lib/proposals/utils/actionGrouping';
import { resolveEntityName } from '@/lib/proposals/utils/entityName';
import { isOnboardingProposal } from '@/lib/proposals/utils/onboardingConfig';
import type { CrmObject } from '@/types/crm';
import { unwrapPropertyValue } from '@/types/crm';

export { resolveEntityName };

/**
 * Convert snake_case string to Title Case, removing ahoy_ prefix.
 */
function snakeCaseToTitleCase(str: string): string {
  return str
    .replace(/^ahoy_/, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get icon component for CRM entity type.
 * Delegates to the centralized EntityTypeIcon registry which resolves icons
 * from the API, hardcoded fallbacks, or a default Folder icon.
 */
export function getEntityTypeIcon(entityType: string): EntityIconComponent {
  return getEntityTypeIconComponent(entityType);
}

/**
 * Get human-readable label for CRM entity type.
 * @deprecated Use getTypeLabelByName from @/lib/crm/utils/getTypeLabel instead.
 */
export function getEntityTypeLabel(entityType: string): string {
  return getTypeLabelByName(entityType);
}

/**
 * Derive entity type from entity_type_name
 * This function returns the entity_type_name as-is since it already represents the entity type.
 * @example "ahoy_contact" -> "ahoy_contact"
 */
export function deriveEntityType(entityTypeName: string): string {
  return entityTypeName;
}

/**
 * Format property value for display
 */
export function formatPropertyValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Format property name to human-readable label
 */
export function formatPropertyLabel(propertyName: string): string {
  return snakeCaseToTitleCase(propertyName);
}

/**
 * Parse association type name to get source and target types
 * e.g., "ahoy_contact_to_company" -> { source: "contact", target: "company" }
 */
export function parseAssociationType(associationTypeName: string): {
  source: string;
  target: string;
} {
  const match = associationTypeName.match(/^ahoy_(\w+)_to_(\w+)$/);
  if (match) {
    return { source: match[1], target: match[2] };
  }
  return { source: 'object', target: 'object' };
}

/**
 * Whether a 'mixed' `TypedActionGroup` renders as the WIDE homogeneous shape
 * — a Name / Property / Current / Proposed diff table (`MixedActionTable`'s
 * "isHomogeneous" fast path) — as opposed to its default single-`Action`-
 * column rendering (one merged create/associate/update description per row).
 *
 * Single source of truth for that homogeneity check, shared by:
 * - `MixedActionTable` itself, to decide its own column set and whether it
 *   should fill its container (a homogeneous group is explicitly kept at the
 *   standard `max-w-[48rem]` width regardless of the caller's preference —
 *   see the comment at its `TableCard.Root`).
 * - `ProposalContent` / `MultiActionTimeline`'s "does every group in this
 *   proposal justify a full-bleed, no-max-width layout" decision. Only the
 *   wide shape does: the default single-column shape has nothing to fill the
 *   extra width with, and stretching it (and the `ActionOverview`/trigger
 *   cards above it, which share the same outer wrapper) just leaves dead
 *   space. `viewType: 'table'` groups are not covered here — they always
 *   carry real property/entity columns by construction and stay full-width.
 *
 * Takes the group's raw (pre-merge) actions: a group that qualifies here can
 * never contain an `ahoy_object_created` action (creates fail the
 * `supportsHomogeneousColumns` check below), and `mergeActions` only ever
 * absorbs actions INTO a create, so there is nothing for it to absorb —
 * pre- and post-merge inputs are equivalent for this specific check.
 */
export function isWideMixedGroup(actions: ProposedAction[]): boolean {
  if (actions.length < MIN_TABLE_VIEW_ACTIONS) {
    return false;
  }
  const firstPayload = actions[0].payload;
  if (!isTimelineEventPayload(firstPayload)) {
    return false;
  }
  const firstEventType = firstPayload.event_type_name;
  const supportsHomogeneousColumns =
    firstEventType === 'ahoy_property_updated' ||
    firstEventType === 'ahoy_association_added' ||
    firstEventType === 'ahoy_association_removed';
  if (!supportsHomogeneousColumns) {
    return false;
  }
  const firstEntityType = firstPayload.entity_type_name;
  const isAssociationEvent =
    firstEventType === 'ahoy_association_added' ||
    firstEventType === 'ahoy_association_removed';
  return actions.every(a => {
    const p = a.payload;
    if (
      !isTimelineEventPayload(p) ||
      p.event_type_name !== firstEventType ||
      p.entity_type_name !== firstEntityType ||
      !a.enriched_data?.object_label
    ) {
      return false;
    }
    // Reject _timeline association types — they are invalid and handled
    // by LinkActionCard's error rendering in standard layout
    if (isAssociationEvent && isAssociationPayload(p)) {
      const assocPayload = p as
        | AssociationAddedEntityPayload
        | AssociationRemovedEntityPayload;
      const { source, target } = parseAssociationType(
        assocPayload.payload.association_type_name
      );
      if (source.endsWith('_timeline') || target.endsWith('_timeline')) {
        return false;
      }
    }
    return true;
  });
}

/**
 * Compute a human-readable label from CRM object properties.
 * Extracts label from the property structure, handling both nested
 * {property_name: {value: actualValue}} and flat property structures.
 * Falls back to "New <Type>" when no name property is set.
 *
 * The name resolution lives in lib (resolveEntityName) so the lib-side action
 * registry renderers share it; this only adds the "New <Type>" fallback.
 */
export function computeObjectLabel(
  properties: Record<string, unknown>,
  entityType: string
): string {
  return (
    resolveEntityName(properties, entityType) ??
    'New ' + getEntityTypeLabel(entityType)
  );
}

/**
 * Get sort priority for onboarding actions.
 * Lower numbers appear first in the list.
 *
 * Fixed onboarding order:
 *  0. Invite your team          (source_id starts with "onboarding_")
 *  1. Review your permissions   (source_id starts with "review_permissions_")
 *  2. Set up your deal pipeline (source_id === "deal_pipeline_template")
 *  3. Customize pipeline        (source_id === "customize_pipeline")
 *  4. Review your import        (source_id starts with "welcome_import_")
 *  5. Everything else / unknown / missing source_id
 */
export function getOnboardingSortPriority(action: {
  source_id?: string | null;
}): number {
  if (!action.source_id) {
    return 5; // Fallback priority for actions without source_id
  }
  if (action.source_id.startsWith('onboarding_')) {
    return 0;
  }
  if (action.source_id.startsWith('review_permissions_')) {
    return 1;
  }
  if (action.source_id === 'deal_pipeline_template') {
    return 2;
  }
  if (action.source_id === 'customize_pipeline') {
    return 3;
  }
  if (action.source_id.startsWith('welcome_import_')) {
    return 4;
  }
  return 5;
}

/**
 * Pick the proposal to auto-select — the one that renders first in the list.
 *
 * Single source of truth for "what's at the top", mirroring ProposalListPanel's
 * display order: onboarding proposals come first, ranked by
 * getOnboardingSortPriority (so the team-invite proposal leads), and
 * everything else follows in the server's order (priority, score, recency).
 * Returns null for an empty list.
 */
export function getFirstProposalToSelect(
  proposals: readonly ProposalListItem[]
): ProposalListItem | null {
  const onboarding = proposals.filter(isOnboardingProposal);
  if (onboarding.length > 0) {
    return [...onboarding].sort(
      (a, b) => getOnboardingSortPriority(a) - getOnboardingSortPriority(b)
    )[0];
  }
  return proposals[0] ?? null;
}

/**
 * Unwraps a property-value wrapper object `{ value: T }` if present, otherwise
 * returns the value as-is. Used by v2 action cards to normalise CRM property
 * values that may arrive either as plain scalars or as wrapped objects.
 */
export function extractValue(prop: unknown): unknown {
  return unwrapPropertyValue(prop);
}

/**
 * The target of `associationTypeName` on a record, or undefined when the
 * record has no such association (or hasn't loaded yet). Used to read the
 * same association off both the live and the projected record.
 */
export function findAssociationTarget(
  object: CrmObject | undefined,
  associationTypeName: string
): string | undefined {
  return object?.associations?.find(
    a => a.association_type_name === associationTypeName
  )?.target_entity_id;
}

/**
 * Build an `EnrichedActionData` from a source/target sub-object on the
 * top-level enrichedData.
 *
 * Association and merge cards render two entity previews (source + target),
 * each needing its own EnrichedActionData for ContentComponent prop builders.
 * The backend provides `target_object` / `source_object` with `{ label,
 * entity_type_name, exists_in_crm }` — this helper maps those fields to the
 * top-level EnrichedActionData shape, falling back to the given label and
 * entityType when the sub-object is absent.
 */
export function buildEnrichedEntityData(
  subObject:
    | {
        label?: string;
        entity_type_name?: string | null;
        exists_in_crm?: boolean;
      }
    | undefined
    | null,
  fallbackLabel: string,
  fallbackEntityType: string
): EnrichedActionData {
  if (subObject) {
    return {
      object_label: subObject.label ?? fallbackLabel,
      entity_type_name: subObject.entity_type_name ?? fallbackEntityType,
      exists_in_crm: subObject.exists_in_crm,
    };
  }
  return {
    object_label: fallbackLabel,
    entity_type_name: fallbackEntityType,
  };
}
